import { getEnvVar } from './env'
import { msalInstance } from './msalInstance'

const ACCOUNT_DELAY = Number(getEnvVar('REACT_APP_ACCOUNT_DELAY')) || 250
const TOKEN_DELAY = Number(getEnvVar('REACT_APP_TOKEN_DELAY')) || 250
const RETRY_DELAY = Number(getEnvVar('REACT_APP_RETRY_DELAY')) || 1000 // Delay between retries
const MAX_RETRIES = Number(getEnvVar('REACT_APP_MAX_RETRIES')) || 5 // Configurable max retries

export type ResponseType = 'arraybuffer' | 'blob' | 'json' | 'text'

export interface ProgressEventLike {
  loaded: number
  total?: number
}

export interface HttpRequestConfig {
  baseURL?: string
  cancelToken?: AbortSignal
  data?: unknown
  headers?: Record<string, string>
  method?: string
  onDownloadProgress?: (progressEvent: ProgressEventLike) => void
  params?: object | URLSearchParams
  responseType?: ResponseType
  signal?: AbortSignal
  url?: string
}

export interface HttpResponse<T = unknown> {
  config?: HttpRequestConfig
  data: T
  headers?: Record<string, string>
  status?: number
  statusText?: string
}

/**
 * Compatibility replacement for a cancellable request source. `token` is a
 * native `AbortSignal` that can be handed to a request via `cancelToken` (or
 * `signal`); calling `cancel()` aborts every in-flight request using it.
 */
export interface CancelTokenSource {
  cancel: (message?: string) => void
  token: AbortSignal
}

/**
 * Error thrown for non-2xx responses that carry no parseable body, and the type
 * used by call sites that previously narrowed with `instanceof AxiosError`.
 */
export class HttpError<T = unknown> extends Error {
  code?: string
  response?: HttpResponse<T>

  constructor(message: string, response?: HttpResponse<T>, code?: string) {
    super(message)
    this.name = 'HttpError'
    this.response = response
    this.code = code
  }
}

export const HttpStatusCode = {
  Accepted: 202,
  BadRequest: 400,
  Created: 201,
  Forbidden: 403,
  InternalServerError: 500,
  NoContent: 204,
  NotFound: 404,
  Ok: 200,
  Unauthorized: 401,
} as const

export interface HttpClient {
  delete<T = unknown>(
    url: string,
    config?: HttpRequestConfig
  ): Promise<HttpResponse<T>>
  get<T = unknown>(
    url: string,
    config?: HttpRequestConfig
  ): Promise<HttpResponse<T>>
  patch<T = unknown>(
    url: string,
    data?: unknown,
    config?: HttpRequestConfig
  ): Promise<HttpResponse<T>>
  post<T = unknown>(
    url: string,
    data?: unknown,
    config?: HttpRequestConfig
  ): Promise<HttpResponse<T>>
  put<T = unknown>(
    url: string,
    data?: unknown,
    config?: HttpRequestConfig
  ): Promise<HttpResponse<T>>
}

const acquireAccessToken = async (): Promise<string> => {
  let account
  while (!account) {
    account = msalInstance.getActiveAccount()
    if (!account) {
      // wait and try again
      await new Promise((resolve) => setTimeout(resolve, ACCOUNT_DELAY))
    }
  }
  let accessToken: string | undefined
  while (!accessToken) {
    try {
      const response = await msalInstance.acquireTokenSilent({
        account,
        scopes: ['user.read'],
      })
      accessToken = response.idToken
    } catch (error) {
      // wait and try again
      await new Promise((resolve) => setTimeout(resolve, TOKEN_DELAY))
    }
  }
  return accessToken
}

/**
 * The acquisition every concurrent caller shares, or null when none is running.
 */
let inFlightTokenRequest: Promise<string> | null = null

/**
 * Resolves the bearer token for a request, coalescing callers that ask at the same
 * time onto a single acquisition.
 *
 * Every request authorizes itself independently, and the pages that fan out — the
 * plans grid most of all — ask for a token many times over in the same tick. Each
 * of those asks used to drive its own `acquireTokenSilent` call and its own
 * account-polling loop.
 *
 * The memo is released as soon as the acquisition settles, so this deliberately
 * coalesces *concurrent* callers rather than caching the token: MSAL keeps its own
 * token cache, which already makes the sequential case cheap, and holding a token
 * here would mean owning its expiry too.
 */
export const getAccessToken = (): Promise<string> => {
  if (inFlightTokenRequest) {
    return inFlightTokenRequest
  }

  const request = acquireAccessToken().finally(() => {
    // Only the acquisition still on record clears it — a later caller may already
    // have installed a fresh one.
    if (inFlightTokenRequest === request) {
      inFlightTokenRequest = null
    }
  })

  inFlightTokenRequest = request

  return request
}

const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms))

const buildSearchParams = (
  params: object | URLSearchParams
): URLSearchParams => {
  if (params instanceof URLSearchParams) {
    return params
  }
  const searchParams = new URLSearchParams()
  Object.entries(params as Record<string, unknown>).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return
    }
    if (Array.isArray(value)) {
      value.forEach((item) => searchParams.append(key, String(item)))
    } else {
      searchParams.append(key, String(value))
    }
  })
  return searchParams
}

const buildUrl = (
  baseURL: string,
  url: string,
  params?: object | URLSearchParams
): string => {
  const normalizedBase = baseURL.endsWith('/') ? baseURL.slice(0, -1) : baseURL
  const normalizedPath =
    url.startsWith('http://') || url.startsWith('https://')
      ? url
      : `${normalizedBase}${url.startsWith('/') ? url : `/${url}`}`

  if (!params) {
    return normalizedPath
  }
  const queryString = buildSearchParams(params).toString()
  if (!queryString) {
    return normalizedPath
  }
  return `${normalizedPath}${
    normalizedPath.includes('?') ? '&' : '?'
  }${queryString}`
}

const isFormData = (value: unknown): value is FormData =>
  typeof FormData !== 'undefined' && value instanceof FormData

const buildHeaders = (
  configHeaders: Record<string, string> | undefined,
  token: string,
  body: unknown
): Record<string, string> => {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-type': 'application/json',
    ...configHeaders,
  }

  if (isFormData(body)) {
    // Let the browser set the multipart boundary itself.
    Object.keys(headers).forEach((key) => {
      if (key.toLowerCase() === 'content-type') {
        delete headers[key]
      }
    })
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`
  }

  return headers
}

const serializeBody = (data: unknown): BodyInit | undefined => {
  if (data === undefined || data === null) {
    return undefined
  }
  if (isFormData(data)) {
    return data
  }
  if (typeof data === 'string') {
    return data
  }
  return JSON.stringify(data)
}

const headersToObject = (headers: Headers): Record<string, string> => {
  const result: Record<string, string> = {}
  headers.forEach((value, key) => {
    result[key] = value
  })
  return result
}

const readBlobWithProgress = async (
  response: Response,
  onDownloadProgress: (progressEvent: ProgressEventLike) => void
): Promise<Blob> => {
  const body = response.body
  if (!body) {
    const fallback = await response.blob()
    onDownloadProgress({ loaded: fallback.size, total: fallback.size })
    return fallback
  }

  const contentLength = response.headers.get('content-length')
  const total = contentLength ? Number(contentLength) : undefined
  const reader = body.getReader()
  const chunks: Uint8Array[] = []
  let loaded = 0

  let done = false
  while (!done) {
    const result = await reader.read()
    done = result.done
    if (result.value) {
      chunks.push(result.value)
      loaded += result.value.length
      onDownloadProgress({ loaded, total })
    }
  }

  return new Blob(chunks as BlobPart[])
}

const parseResponseBody = async (
  response: Response,
  config: HttpRequestConfig
): Promise<unknown> => {
  switch (config.responseType) {
    case 'arraybuffer':
      return response.arrayBuffer()
    case 'blob':
      if (config.onDownloadProgress) {
        return readBlobWithProgress(response, config.onDownloadProgress)
      }
      return response.blob()
    case 'text':
      return response.text()
    default: {
      const text = await response.text()
      if (!text) {
        return undefined
      }
      try {
        return JSON.parse(text)
      } catch {
        return text
      }
    }
  }
}

const buildRejection = async (
  response: Response,
  config: HttpRequestConfig
): Promise<unknown> => {
  const body = await parseResponseBody(response, config)
  if (body !== undefined && body !== null && body !== '') {
    return body
  }
  return new HttpError(response.statusText || `Request failed`, {
    config,
    data: body,
    headers: headersToObject(response.headers),
    status: response.status,
    statusText: response.statusText,
  })
}

const isRetryableAuthStatus = (status: number): boolean =>
  status === 401 || status === 403

const performRequest = async <T>(
  baseURL: string,
  method: string,
  url: string,
  data: unknown,
  config: HttpRequestConfig
): Promise<HttpResponse<T>> => {
  const requestUrl = buildUrl(baseURL, url, config.params)
  const body = serializeBody(data)
  const signal = config.signal ?? config.cancelToken

  let attempt = 0
  for (;;) {
    const token = await getAccessToken()
    const response = await fetch(requestUrl, {
      body,
      headers: buildHeaders(config.headers, token, data),
      method,
      signal,
    })

    if (response.ok) {
      const responseData = (await parseResponseBody(response, config)) as T
      return {
        config,
        data: responseData,
        headers: headersToObject(response.headers),
        status: response.status,
        statusText: response.statusText,
      }
    }

    if (isRetryableAuthStatus(response.status)) {
      if (attempt < MAX_RETRIES) {
        attempt += 1
        await delay(RETRY_DELAY)
        continue
      }
      return Promise.reject(new HttpError('Max retries reached'))
    }

    return Promise.reject(await buildRejection(response, config))
  }
}

const createClient = (baseURL: string): HttpClient => ({
  // DELETE carries its payload on the config rather than as a positional
  // argument, so `config.data` is the request body.
  delete: <T = unknown>(url: string, config: HttpRequestConfig = {}) =>
    performRequest<T>(baseURL, 'DELETE', url, config.data, config),
  get: <T = unknown>(url: string, config: HttpRequestConfig = {}) =>
    performRequest<T>(baseURL, 'GET', url, undefined, config),
  patch: <T = unknown>(
    url: string,
    data?: unknown,
    config: HttpRequestConfig = {}
  ) => performRequest<T>(baseURL, 'PATCH', url, data, config),
  post: <T = unknown>(
    url: string,
    data?: unknown,
    config: HttpRequestConfig = {}
  ) => performRequest<T>(baseURL, 'POST', url, data, config),
  put: <T = unknown>(
    url: string,
    data?: unknown,
    config: HttpRequestConfig = {}
  ) => performRequest<T>(baseURL, 'PUT', url, data, config),
})

export const instance = createClient(getEnvVar('REACT_APP_BFF_HOST') ?? '')

export const planServiceInstance = createClient(
  getEnvVar('REACT_APP_PLAN_SERVICE_HOST') ?? ''
)
